import { collection, doc, serverTimestamp, setDoc } from 'firebase/firestore';

import { useAuth } from '../contextProviders/AuthProvider';
import { useData } from '../contextProviders/DataProvider';
import { firestore } from '../firebase';
import { USGA_POST_BATCH_MAX_ITEMS, USGA_POST_BATCH_REASONS, USGA_POST_BATCH_STATUSES } from '../types/UsgaPostBatch';

import type Event from '../types/Event';
import type { UsgaPostBatch, UsgaPostBatchItem } from '../types/UsgaPostBatch';

/** One score to post (or confirm); requestedBy is filled in from the signed-in user. */
export type UsgaPostRequestItem = Omit<UsgaPostBatchItem, 'requestedBy'>;

export const useUsgaPostMutators = () => {
  const { isGod } = useAuth();
  const { userState } = useData();

  /**
   * Asks the server to post scores to the USGA: one usgaPostBatch request per up to
   * USGA_POST_BATCH_MAX_ITEMS scores (handleUsgaPostBatchRequested). God only while on staging.
   */
  const requestUsgaPosts = async (event: Event, items: UsgaPostRequestItem[], skipSeasonCheck = false): Promise<void> => {
    if (!isGod) throw new Error('Only God can post scores to the USGA while posting is on staging.');
    const requestedBy = userState.status === 'ready' ? userState.userProfile?.itemId : undefined;
    if (!requestedBy) throw new Error('Not signed in.');

    for (let i = 0; i < items.length; i += USGA_POST_BATCH_MAX_ITEMS) {
      const ref = doc(collection(firestore, `org/${event.orgId}/event/${event.itemId}/usgaPostBatch`));
      const batch: UsgaPostBatch = {
        itemId: ref.id,
        orgId: event.orgId,
        eventId: event.itemId,
        status: USGA_POST_BATCH_STATUSES.REQUESTED,
        reason: USGA_POST_BATCH_REASONS.ADMIN,
        items: items.slice(i, i + USGA_POST_BATCH_MAX_ITEMS).map((item) => ({ ...item, requestedBy })),
        skipSeasonCheck,
        requestedBy,
        requestedAt: serverTimestamp(),
        startedAt: null,
        finishedAt: null,
        postedCount: null,
        waitingCount: null,
        errorCount: null,
        skippedCount: null,
      };
      await setDoc(ref, batch);
    }
  };

  return { requestUsgaPosts };
};
